import React, { useEffect, useState } from "react";
import { getFinalizedApplication } from "../../../services/application.services";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { Download } from "lucide-react";

const FinalizedApplication = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [applicationsPerPage, setApplicationsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  // Fetch applications data
  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      try {
        const data = await getFinalizedApplication();

        // Process the data to flatten it
        const flattened = [];
        data.forEach((student) => {
          const {
            first_name,
            last_name,
            email,
            finalized_universities = [],
            application_manager = {},
          } = student;

          finalized_universities.forEach((uni) => {
            flattened.push({
              studentName: `${first_name} ${last_name}`,
              email: email,
              phone: student.phone, // <-- Add this line
              status: uni.status || "",
              country: uni.country || "",
              university: uni.university || "",
              course: uni.course || "",
              applicationManager: application_manager.name || "",
            });
          });
        });

        setApplications(flattened);
      } catch (error) {
        console.error("Error fetching finalized applications:", error);
        toast.error("Failed to load applications");
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  // Sort function
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Function to handle Excel export
  const handleDownloadExcel = () => {
    if (filteredApplications.length === 0) {
      toast.error("No data available to export!");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(filteredApplications);

    // Set column widths
    const widths = [
      { wch: 5 }, // Sr No (added in filteredApplications)
      { wch: 20 }, // Student Name
      { wch: 30 }, // Email
      { wch: 15 }, // Status
      { wch: 15 }, // Country
      { wch: 25 }, // University
      { wch: 25 }, // Course
      { wch: 20 }, // Application Manager
    ];

    worksheet["!cols"] = widths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Finalized Applications");

    const fileName = `Finalized_Applications_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    XLSX.writeFile(workbook, fileName);

    toast.success("Excel file downloaded successfully!");
  };

  // Filter and sort applications
  const filteredApplications = React.useMemo(() => {
    let result = applications;

    // Filter based on search term if provided
    if (searchTerm) {
      result = applications.filter(
        (app) =>
          app.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.university?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.country?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.course?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.applicationManager
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Sort the data if sort config is set
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        // Handle null/undefined values
        if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;
        if (!a[sortConfig.key])
          return sortConfig.direction === "ascending" ? -1 : 1;
        if (!b[sortConfig.key])
          return sortConfig.direction === "ascending" ? 1 : -1;

        // For standard string comparison
        if (a[sortConfig.key].toLowerCase() < b[sortConfig.key].toLowerCase()) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key].toLowerCase() > b[sortConfig.key].toLowerCase()) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [applications, searchTerm, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(
    filteredApplications.length / applicationsPerPage
  );
  const indexOfLastApp = currentPage * applicationsPerPage;
  const indexOfFirstApp = indexOfLastApp - applicationsPerPage;
  const currentApplications = filteredApplications.slice(
    indexOfFirstApp,
    indexOfLastApp
  );

  // Function to get status badge color
  const getStatusBadgeClass = (status) => {
    status = status.toLowerCase();
    if (status.includes("approved") || status.includes("accepted")) {
      return "bg-green-100 text-green-800";
    } else if (status.includes("pending") || status.includes("waiting")) {
      return "bg-yellow-100 text-yellow-800";
    } else if (status.includes("rejected") || status.includes("declined")) {
      return "bg-red-100 text-red-600";
    } else {
      return "bg-blue-100 text-blue-800";
    }
  };

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      {/* Header with search and export */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <h2 className="text-xl font-semibold text-slate-800">
          Finalized Applications
          {applications.length > 0 && (
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({filteredApplications.length} applications)
            </span>
          )}
        </h2>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {/* Search box */}
          <div className="relative w-full sm:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search applications..."
              className="w-full rounded-xl border border-slate-300 py-2 pl-10 pr-4 text-slate-700 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
            />
          </div>

          {/* Export button */}
          <button
            onClick={handleDownloadExcel}
            className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 font-medium text-white transition-colors hover:bg-indigo-700"
            disabled={filteredApplications.length === 0}
          >
            <Download size={20} />
            <span>Export Excel</span>
          </button>
        </div>
      </div>

      {/* Records per page selector */}
      <div className="mb-4 flex justify-end">
        <label className="flex items-center text-sm text-gray-600">
          <span className="mr-2">Show</span>
          <select
            value={applicationsPerPage}
            onChange={(e) => {
              setApplicationsPerPage(Number(e.target.value));
              setCurrentPage(1); // Reset to first page when changing page size
            }}
            className="border border-gray-300 rounded px-2 py-1"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span className="ml-2">entries</span>
        </label>
      </div>

      {/* Table */}
      <div className="w-full overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-slate-100/80 text-sm leading-normal text-slate-700">
              <th className="py-3 px-6 text-left">Sr No</th>
              <th
                className="py-3 px-6 text-left cursor-pointer"
                onClick={() => requestSort("studentName")}
              >
                Student Name{" "}
                {sortConfig.key === "studentName" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="py-3 px-6 text-left cursor-pointer"
                onClick={() => requestSort("email")}
              >
                Email{" "}
                {sortConfig.key === "email" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="py-3 px-6 text-left cursor-pointer"
                onClick={() => requestSort("email")}
              >
                Phone{" "}
                {sortConfig.key === "email" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="py-3 px-6 text-left cursor-pointer"
                onClick={() => requestSort("status")}
              >
                Status{" "}
                {sortConfig.key === "status" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="py-3 px-6 text-left hidden md:table-cell cursor-pointer"
                onClick={() => requestSort("country")}
              >
                Country{" "}
                {sortConfig.key === "country" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="py-3 px-6 text-left cursor-pointer"
                onClick={() => requestSort("university")}
              >
                University{" "}
                {sortConfig.key === "university" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="py-3 px-6 text-left hidden sm:table-cell cursor-pointer"
                onClick={() => requestSort("course")}
              >
                Course{" "}
                {sortConfig.key === "course" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="py-3 px-6 text-left hidden lg:table-cell cursor-pointer"
                onClick={() => requestSort("applicationManager")}
              >
                Application Manager{" "}
                {sortConfig.key === "applicationManager" && (
                  <span>
                    {sortConfig.direction === "ascending" ? "↑" : "↓"}
                  </span>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="py-4 px-6 text-center">
                  <div className="flex justify-center items-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
                    <span className="text-base">Loading applications...</span>
                  </div>
                </td>
              </tr>
            ) : currentApplications.length === 0 ? (
              <tr>
                <td colSpan="8" className="py-4 px-6 text-center text-gray-500">
                  {searchTerm ? (
                    <div>
                      <p className="font-medium text-base">
                        No matching records found
                      </p>
                      <p className="text-base mt-2">
                        Try adjusting your search criteria
                      </p>
                    </div>
                  ) : (
                    <p className="font-medium text-base">
                      No finalized applications available
                    </p>
                  )}
                </td>
              </tr>
            ) : (
              currentApplications.map((app, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="py-3 px-6 text-base">
                    {indexOfFirstApp + index + 1}
                  </td>
                  <td className="py-3 px-6 text-base font-medium">
                    {app.studentName}
                  </td>
                  <td className="py-3 px-6 text-base">{app.email}</td>
                  <td className="py-3 px-6 text-base">
                    {app.phone ? `+${app.phone}` : "-"}
                  </td>

                  <td className="py-3 px-6">
                    <span
                      className={`px-3 py-1 inline-flex text-sm font-medium rounded-full ${
                        app.status
                          ? getStatusBadgeClass(app.status)
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {app.status || "—"}
                    </span>
                  </td>
                  <td className="py-3 px-6 text-base hidden md:table-cell">
                    {app.country || "—"}
                  </td>
                  <td className="py-3 px-6 text-base">
                    {app.university || "—"}
                  </td>
                  <td className="py-3 px-6 text-base hidden sm:table-cell">
                    {app.course || "—"}
                  </td>
                  <td className="py-3 px-6 text-base hidden lg:table-cell">
                    {app.applicationManager || "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {!loading && totalPages > 0 && (
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center">
          <div className="text-sm text-gray-600 mb-3 sm:mb-0">
            Showing {indexOfFirstApp + 1} to{" "}
            {Math.min(indexOfLastApp, filteredApplications.length)} of{" "}
            {filteredApplications.length} entries
          </div>

          <div className="flex">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="mx-1 px-3 py-1 border rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {Array.from(
              { length: Math.min(totalPages, 5) },
              (_, i) => i + 1
            ).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`mx-1 w-8 h-8 flex items-center justify-center border rounded ${
                  currentPage === page
                    ? "bg-gray-200 border-gray-300"
                    : "bg-white border-gray-300 hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || totalPages === 0}
              className="mx-1 px-3 py-1 border rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinalizedApplication;
